import {
  Flex,
  Icon,
  sizes,
  Skeleton,
  spaces,
  Text,
  Tooltip,
} from '@electricjs/arc';
import { skipToken } from '@reduxjs/toolkit/query';

import {
  buildOptionsFromResource,
  isEmptySettingValue,
  resolveValueToLabel,
} from '@/components/Applications/ApplicationDetails/Settings/settingsUtils';
import { SupportedSettingTypes } from '@/components/Applications/ApplicationDetails/Settings/settingTypes';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { useGetAppIntegrationResourceQuery } from '@/redux/slices/integrationsApiSlice';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useFormatSettingValue } from '@/hooks/useFormatSettingValue';
import type { ResourceOption } from '@/components/Applications/ApplicationDetails/Settings/types';

const EMPTY_VALUE_PLACEHOLDER = '—';

type SettingValueResolverProps = {
  applicationKey: string;
  settingName: string;
  value: string | string[] | null;
  configuredCountText: string;
  type?: string;
};

/**
 * Component that resolves a setting value to its display name
 * by fetching the resource from the integration API.
 */
export const SettingValueResolver = ({
  applicationKey,
  settingName,
  value,
  configuredCountText,
  type,
}: SettingValueResolverProps) => {
  const organizationId = useGetOrganizationId();

  const isCheckbox = type === SupportedSettingTypes.Checkbox;
  const isText =
    type === SupportedSettingTypes.Text || type === SupportedSettingTypes.Email;

  const { data: resourceData, isLoading } = useGetAppIntegrationResourceQuery(
    settingName && !isCheckbox && !isText
      ? { organizationId, applicationKey, resourceSlug: settingName }
      : skipToken
  );

  const { applicationSettingsRedesign } = useFlags();
  const formatSettingValue = useFormatSettingValue();

  const options: ResourceOption[] = (() => {
    if (!resourceData?.resource) return [];
    return buildOptionsFromResource(
      resourceData.resourceLabel,
      resourceData.resourceValue,
      resourceData.resource
    );
  })();

  const isArray = Array.isArray(value);

  if (isCheckbox) {
    const isChecked = value === 'true';
    return isChecked ? (
      <Icon icon={'check-circle'} intent={'success'} size="small" />
    ) : (
      <Icon icon={'circle-xmark'} intent={'error'} size="small" />
    );
  }

  if (isLoading) {
    return <Skeleton width={sizes.size_32_rem} />;
  }

  if (isEmptySettingValue(value) || value === null) {
    return <Text variant="body">{EMPTY_VALUE_PLACEHOLDER}</Text>;
  }

  if (isArray) {
    const resolvedValues = value
      .map(val => resolveValueToLabel(val, options))
      .sort((a, b) => a.localeCompare(b));

    if (applicationSettingsRedesign) {
      if (resolvedValues.length === 1) {
        return (
          <>
            <Text variant="body-2">{resolvedValues[0]}</Text>
          </>
        );
      } else if (resolvedValues.length > 1) {
        return (
          <>
            <Text variant="body-2">{configuredCountText}</Text>
            <Tooltip
              content={
                <Flex flexDirection="column" justifyContent="space-between">
                  {resolvedValues.map((item, index) => {
                    return (
                      <Text
                        key={index}
                        variant="body-2"
                        overflow="hidden"
                        whiteSpace="nowrap"
                        textOverflow="ellipsis">
                        {item}
                      </Text>
                    );
                  })}
                </Flex>
              }>
              <Icon
                pl="1"
                icon="information-circle"
                size="small"
                intent="neutral"
              />
            </Tooltip>
          </>
        );
      } else {
        return <Text variant="body-2">{EMPTY_VALUE_PLACEHOLDER}</Text>;
      }
    } else {
      return (
        <>
          <Text variant="body">{configuredCountText}</Text>
          <Flex vertical gap={spaces.space_3xs_rem} mt={spaces.space_3xs_rem}>
            {resolvedValues.map((item, index) => (
              <Text key={index} variant="legal" intent="secondary">
                {item}
              </Text>
            ))}
          </Flex>
        </>
      );
    }
  }

  const resolvedValue = formatSettingValue(
    settingName,
    value,
    resolveValueToLabel(value, options)
  );

  return <Text variant="body-2">{resolvedValue}</Text>;
};
